import { useEffect, useRef, useCallback } from 'react';

import { FormattedMessage } from 'react-intl';

import classNames from 'classnames';

import type { Map as ImmutableMap, List as ImmutableList } from 'immutable';

import { undoUploadCompose } from 'mastodon/actions/compose';
import { openModal } from 'mastodon/actions/modal';
import { Blurhash } from 'mastodon/components/blurhash';
import { Icon } from 'mastodon/components/icon';
import type { MediaAttachment } from 'mastodon/models/media_attachment';
import { useAppDispatch, useAppSelector } from 'mastodon/store';
import { CloseIcon } from 'mastodon/components/icons/close';
import { EditIcon } from 'mastodon/components/icons/edit';

export const Upload: React.FC<{
  id: string;
  dragging?: boolean;
  overlay?: boolean;
  tall?: boolean;
  wide?: boolean;
  remainingCount?: number;
  onOverlayClick?: () => void;
}> = ({ id, overlay, tall, wide, remainingCount = 0, onOverlayClick }) => {
  const dispatch = useAppDispatch();
  const media = useAppSelector((state) =>
    (
      (state.compose as ImmutableMap<string, unknown>).get(
        'media_attachments',
      ) as ImmutableList<MediaAttachment>
    ).find((item) => item.get('id') === id),
  );
  const sensitive = useAppSelector(
    (state) => state.compose.get('spoiler') as boolean,
  );

  const mediaAttachment = useAppSelector(
    (state) => state.compose.get('media_attachment'),
  );

  const handleUndoClick = useCallback(() => {
    dispatch(undoUploadCompose(id));
  }, [dispatch, id]);

  const handleImageEditClick = useCallback(() => {
    dispatch(
      openModal({ modalType: 'IMAGE_EDIT', modalProps: { mediaId: id } }),
    );
  }, [dispatch, id]);

  const thumbnailRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (media && !sensitive && thumbnailRef.current) {
      const scrollToThumbnail = () => {
        requestAnimationFrame(() => {
          thumbnailRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        });
      };

      const timeout = setTimeout(scrollToThumbnail, 100);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [media, sensitive]);

  if (!media) {
    return null;
  }

  const focusX = media.getIn(['meta', 'focus', 'x']) as number;
  const focusY = media.getIn(['meta', 'focus', 'y']) as number;
  const x = (focusX / 2 + 0.5) * 100;
  const y = (focusY / -2 + 0.5) * 100;
  const missingDescription =
    ((media.get('description') as string | undefined) ?? '').length === 0;

  return (
    <div
      className={classNames('compose-form__upload media-gallery__item', {
        overlay,
        'media-gallery__item--tall': tall,
        'media-gallery__item--wide': wide,
      })}
    >
      <div
        ref={thumbnailRef}
        className='compose-form__upload__thumbnail'
        style={{
          backgroundImage: !sensitive
            ? `url(${media.get('preview_url') as string})`
            : undefined,
          backgroundPosition: `${x}% ${y}%`,
        }}
      >
        {sensitive && (
          <Blurhash
            hash={media.get('blurhash') as string}
            className='compose-form__upload__preview'
          />
        )}

        <div className='compose-form__upload__actions'>
          <button
            type='button'
            className='icon-button compose-form__upload__delete'
            onClick={handleUndoClick}
          >
            <Icon id='close' icon={CloseIcon} />
          </button>
          {media.get('type') === 'image' &&
            media.get('file_content_type') !== 'image/gif' && (
              <button
                type='button'
                className='icon-button'
                onClick={handleImageEditClick}
              >
                <Icon id='edit' icon={EditIcon} />{' '}
                <FormattedMessage id='upload_form.edit' defaultMessage='Edit' />
              </button>
            )}
        </div>
      </div>

      {remainingCount > 0 && (
        <div className='media-gallery__more-overlay' onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onOverlayClick?.();
        }}>
          <span className='media-gallery__more-overlay__count'>+{remainingCount}</span>
        </div>
      )}
    </div>
  );
};
